import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { TimelineSmsMessage } from './TimelineSms';
import '../../style/scss/sms.scss';

interface SmsMessageViewerProps {
    smsMessage: TimelineSmsMessage;
    displayDate: string;
}

const SmsMessageViewer: React.FC<SmsMessageViewerProps> = ({ smsMessage, displayDate }) => {
    const { t } = useTranslation();
    const { revealModal } = useModal();

    // Reveal the modal on the first render
    useEffect(() => {
        revealModal();
    }, []);

    // Determine the display state of the message
    const isScheduled = smsMessage.status === 'scheduled' || smsMessage.status === 'retrying';

    // Build the recipient value, for scheduled messages the expected recipient
    const recipientName = isScheduled ? smsMessage.preview_recipient_name : smsMessage.recipient_name;
    const phoneNumber = isScheduled ? smsMessage.preview_phone_number : smsMessage.phone_number;
    const recipient = recipientName
        ? `${recipientName}${phoneNumber ? ` (${phoneNumber})` : ''}`
        : phoneNumber || '-';

    // Show the sent body or the rendered preview of a scheduled message
    const body = smsMessage.body || smsMessage.preview_body;

    return (
        <div className='sms-message-viewer'>
            {/* The recipient of the message */}
            <div className='sms-viewer-row'>
                <span className='sms-viewer-label'>
                    {isScheduled ? t('timeline.sms.viewer_planned_recipient_label') : t('timeline.sms.viewer_recipient_label')}
                </span>
                <span>{recipient}</span>
            </div>

            {/* The planned or sent moment */}
            <div className='sms-viewer-row'>
                <span className='sms-viewer-label'>
                    {isScheduled ? t('timeline.sms.viewer_scheduled_label') : t('timeline.sms.viewer_sent_label')}
                </span>
                <span>{displayDate}</span>
            </div>

            {/* The status of the message */}
            <div className='sms-viewer-row'>
                <span className='sms-viewer-label'>{t('timeline.sms.viewer_status_label')}</span>
                <span>
                    {t(`sms_message.status.${smsMessage.status}`)}
                    {smsMessage.skip_reason && ` (${t(`sms_message.skip_reason.${smsMessage.skip_reason}`)})`}
                </span>
            </div>

            {/* The sender of a manual message */}
            {smsMessage.sent_by_user_name && (
                <div className='sms-viewer-row'>
                    <span className='sms-viewer-label'>{t('timeline.sms.viewer_sent_by_label')}</span>
                    <span>{smsMessage.sent_by_user_name}</span>
                </div>
            )}

            {/* The message body as an sms bubble */}
            {body && (
                <div className='sms-viewer-body'>{body}</div>
            )}
        </div>
    );
};

export default SmsMessageViewer;
